import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import callOrLog from 'src/common/utils/callOrLog';
import { SEARCH } from 'common/utils/EventTracker/utils/eventConstants';
import SearchBarTableRow from './SearchBarTableRow';
import SearchBarTableHeader from './SearchBarTableHeader';
import { goToSearchIndex } from '../utils';

const SearchBarTable = ({ model, numResults, sectionResults }, context) => {
  const {
    closeSearchResults,
    eventTracker,
    query,
  } = context;
  const tableRef = useRef(null);
  const rowRefs = useRef([]);
  const viewAllLinkRef = useRef(null);

  const onViewAllClick = () => {
    closeSearchResults();
    callOrLog(() => eventTracker(SEARCH.viewAllResults, { model }));
    goToSearchIndex({ query, model });
  };

  const totalRows = Math.min(numResults, sectionResults.length);

  const navigateToRow = (nextIndex) => {
    if (nextIndex >= 0 && nextIndex < totalRows) {
      const nextRow = rowRefs.current[nextIndex];
      if (nextRow) nextRow.focus();
    } else if (nextIndex === totalRows) {
      if (viewAllLinkRef.current) {
        viewAllLinkRef.current.focus();
      }
    }
  };

  const handleViewAllKeyDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    switch (e.key) {
      case 'ArrowDown':
      case 'Tab':
      case 'ArrowUp':
        tableRef.current.focus();
        break;
      case ' ':
      case 'Enter':
        onViewAllClick();
        break;
      default:
        break;
    }
  };

  return (
    <div className="search-results-section-table">
      <div id={`search-result-description-${model}`} className="sr-only">
        Press Enter, Space or click to view Clients FaceSheet. Use arrow keys to navigate.
      </div>
      <table
        ref={tableRef}
        className="search-results-section-table__table"
        role="grid"
        tabIndex={-1}
      >
        <thead>
          <SearchBarTableHeader model={model} />
        </thead>
        <tbody>
          {
            _.take(sectionResults, numResults).map((result, key) => (
              <SearchBarTableRow
                index={key}
                key={`${model}-${key}`}
                model={model}
                result={result}
                totalRows={totalRows}
                navigateToRow={navigateToRow}
                rowRef={(element) => { rowRefs.current[key] = element; }}
              />
            ))
          }
        </tbody>
      </table>

      <div className="search-results-section-table__view-all">
        <a
          id={`search-results-section-table__view-all-${model}`}
          ref={viewAllLinkRef}
          onClick={onViewAllClick}
          onKeyDown={handleViewAllKeyDown}
          tabIndex={-1}
        >
          View All
        </a>
      </div>
    </div>
  );
};

SearchBarTable.propTypes = {
  numResults: PropTypes.number.isRequired,
  model: PropTypes.string.isRequired,
  sectionResults: PropTypes.array.isRequired,
};

SearchBarTable.contextTypes = {
  closeSearchResults: PropTypes.func.isRequired,
  eventTracker: PropTypes.func.isRequired,
  query: PropTypes.string.isRequired,
};

export default SearchBarTable;
